import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {environment} from '../../environments/environment';
import {DataService} from '../services/data.service';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {getOrderSourceLabel} from '../services/order-source-labels';
import {TableSortState} from '../table-sort';
import {GoogleMap, MapInfoWindow, MapMarker} from '@angular/google-maps';

@Component({
    selector: 'app-vouchers',
    templateUrl: './vouchers.component.html',
    styleUrls: ['./vouchers.component.css']
})
export class VouchersComponent implements OnInit, OnDestroy {

    @ViewChild('voucherOrderMap', {static: false}) voucherOrderMap: GoogleMap;
    @ViewChild('voucherInfoWindow', {read: MapInfoWindow, static: false}) voucherInfoWindow: MapInfoWindow;

    pendingVouchers: Array<any> = [];
    filteredVouchers: Array<any> = [];
    searchText = '';
    carFilter = '';
    clientFilter = '';
    currentPage = 1;
    pageSize = 25;
    selectedVoucher: any;
    selectedVoucherSource: any;
    editableVoucherIds = '';
    voucherEditMode = '';
    activeVoucherTab = 'payment';
    selectedVoucherOrderWrapper: any;
    selectedOrderSummary: any;
    selectedOrderRoutePoints: Array<any> = [];
    selectedOrderMapMarkers: Array<any> = [];
    selectedOrderMapLat = 44.429021;
    selectedOrderMapLng = 26.103849;
    selectedOrderMapCenter: google.maps.LatLngLiteral = {
        lat: this.selectedOrderMapLat,
        lng: this.selectedOrderMapLng
    };
    selectedOrderMapZoom = 13;
    selectedOrderInfoMarker: any;
    payMessage = '';
    payMessageType = '';
    isLoadingVouchers = true;
    isPayingVoucher = false;
    imageZoom = 1;
    tableSort = new TableSortState();
    isDraggingPhoto = false;
    private photoDragStartX = 0;
    private photoDragStartY = 0;
    private photoDragScrollLeft = 0;
    private photoDragScrollTop = 0;
    private shouldFitSelectedOrderMap = false;

    private dataChangedSessionSubscription: Subscription;

    constructor(
        private dataService: DataService,
        private restService: RestService,
        private uiBlockerService: UIBlockerService
    ) { /* noop */
    }

    ngOnInit() {
        this.dataChangedSessionSubscription = this.dataService.dataChangedSession.subscribe(() => {
            this.loadPendingVouchers();
        });

        this.loadPendingVouchers();
    }

    ngOnDestroy() {
        if (!isNullOrUndefined(this.dataChangedSessionSubscription)) {
            this.dataChangedSessionSubscription.unsubscribe();
        }
        this.stopPhotoDrag();
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        if (isNullOrUndefined(this.selectedVoucher)) {
            return;
        }

        event.preventDefault();
        this.closeDetails();
    }

    @HostListener('document:mouseup')
    onDocumentMouseUp() {
        this.stopPhotoDrag();
    }

    loadPendingVouchers() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier())) {
            this.isLoadingVouchers = false;
            return;
        }

        this.isLoadingVouchers = true;
        this.restService.getPendingVouchers()
            .pipe(
                finalize(() => {
                    this.isLoadingVouchers = false;
                    this.uiBlockerService.unBlockUI();
                }),
                tap(
                    () => { /* noop */
                    },
                    () => { /* noop */
                    }
                )
            )
            .subscribe((res: any) => {
                this.pendingVouchers = this.getVouchersFromResponse(res);
                this.filterVouchers(false);
            });
    }

    onSearchTextChanged(value: string) {
        this.searchText = isNullOrUndefined(value) ? '' : value;
        this.filterVouchers(true);
    }

    filterVouchers(resetPage: boolean) {
        const search = this.searchText.trim().toLowerCase();
        if (search === '') {
            this.filteredVouchers = this.pendingVouchers.filter(voucher => this.matchesVoucherFilters(voucher));
        } else {
            this.filteredVouchers = this.pendingVouchers.filter(voucher =>
                this.matchesVoucherFilters(voucher)
                && (this.contains(voucher.ClientName, search)
                    || this.contains(voucher.VoucherId, search)
                    || this.contains(voucher.Amount, search)
                    || this.contains(this.getAmount(voucher.Amount), search))
            );
        }

        if (resetPage) {
            this.currentPage = 1;
        }
        this.clampCurrentPage();
    }

    onCarFilterChanged(value: string) {
        this.carFilter = value;
        this.filterVouchers(true);
    }

    onClientFilterChanged(value: string) {
        this.clientFilter = value;
        this.filterVouchers(true);
    }

    getCarFilterOptions() {
        return this.getDistinctOptions(voucher => voucher.CarIdentifier);
    }

    getClientFilterOptions() {
        return this.getDistinctOptions(voucher => voucher.ClientName);
    }

    getPagedVouchers() {
        const sortedVouchers = this.tableSort.sort(
            this.filteredVouchers,
            (voucher, column) => this.getVoucherSortValue(voucher, column)
        );
        const start = (this.currentPage - 1) * this.pageSize;
        return sortedVouchers.slice(start, start + this.pageSize);
    }

    changePageSize() {
        this.currentPage = 1;
    }

    getTotalPages() {
        return Math.max(1, Math.ceil(this.filteredVouchers.length / this.pageSize));
    }

    getVisiblePages() {
        const totalPages = this.getTotalPages();
        const start = Math.max(1, Math.min(this.currentPage - 2, totalPages - 4));
        const end = Math.min(totalPages, start + 4);
        const pages = [];
        for (let page = start; page <= end; page++) {
            pages.push(page);
        }
        return pages;
    }

    getFirstItemNumber() {
        return this.filteredVouchers.length === 0 ? 0 : ((this.currentPage - 1) * this.pageSize) + 1;
    }

    getLastItemNumber() {
        return Math.min(this.currentPage * this.pageSize, this.filteredVouchers.length);
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.getTotalPages()) {
            this.currentPage = page;
        }
    }

    hasOrder(voucher: any) {
        return !isNullOrUndefined(voucher)
            && !isNullOrUndefined(voucher.OrderIdentifier)
            && String(voucher.OrderIdentifier).trim() !== '';
    }

    private getVoucherSortValue(voucher: any, column: string) {
        switch (column) {
            case 'created':
                return this.getTimeValue(voucher.CreatedAt);
            case 'client':
                return voucher.ClientName;
            case 'car':
                return voucher.CarIdentifier;
            case 'order':
                return this.hasOrder(voucher);
            case 'amount': {
                const amount = Number(voucher.Amount);
                return isNaN(amount) ? undefined : amount;
            }
            case 'voucher':
                return voucher.VoucherId;
            default:
                return '';
        }
    }

    private getTimeValue(value: any) {
        if (isNullOrUndefined(value) || value === '') {
            return undefined;
        }
        const time = new Date(value).getTime();
        return isNaN(time) ? undefined : time;
    }

    private contains(value: any, search: string) {
        return !isNullOrUndefined(value) && String(value).toLowerCase().indexOf(search) >= 0;
    }

    private matchesVoucherFilters(voucher: any) {
        return (this.carFilter === '' || voucher.CarIdentifier === this.carFilter)
            && (this.clientFilter === '' || voucher.ClientName === this.clientFilter);
    }

    private getDistinctOptions(valueGetter: (voucher: any) => any) {
        const values = this.pendingVouchers
            .map(valueGetter)
            .filter(value => !isNullOrUndefined(value) && String(value).trim() !== '')
            .map(value => String(value));
        return values.filter((value, index) => values.indexOf(value) === index)
            .sort((left, right) => left.localeCompare(right));
    }

    private clampCurrentPage() {
        this.currentPage = Math.max(1, Math.min(this.currentPage, this.getTotalPages()));
    }

    openDetails(voucher: any) {
        this.selectedVoucherSource = voucher;
        this.selectedVoucher = Object.assign({}, voucher);
        this.voucherEditMode = this.getVoucherEditMode(voucher);
        this.editableVoucherIds = this.formatVoucherIds(voucher);
        this.activeVoucherTab = 'payment';
        this.setSelectedOrderData(voucher);
        this.clearPayMessage();
        this.imageZoom = 1;
        this.stopPhotoDrag();
    }

    closeDetails() {
        if (this.isPayingVoucher) {
            return;
        }

        this.resetSelectedVoucher();
    }

    payVoucher(voucher: any) {
        if (isNullOrUndefined(voucher) || this.isPayingVoucher) {
            return;
        }

        this.isPayingVoucher = true;
        this.clearPayMessage();
        const voucherToPay = this.buildPayVoucherPayload(voucher);
        this.restService.payPendingVoucher(voucherToPay)
            .pipe(
                finalize(() => {
                    this.isPayingVoucher = false;
                    this.uiBlockerService.unBlockUI();
                }),
                tap(
                    () => { /* noop */
                    },
                    () => { /* noop */
                    }
                )
            )
            .subscribe((res: any) => {
                if (this.isPayResponseSuccessful(res)) {
                    this.resetSelectedVoucher();
                    this.loadPendingVouchers();
                } else {
                    this.setPayMessage(this.getResponseMessage(res, 'Failed to pay voucher.'), 'error');
                }
            }, (error: any) => {
                this.setPayMessage(this.getResponseMessage(error, 'Failed to pay voucher.'), 'error');
            });
    }

    zoomIn() {
        if (this.imageZoom < 3) {
            this.imageZoom = Math.round((this.imageZoom + 0.25) * 100) / 100;
        }
    }

    zoomOut() {
        if (this.imageZoom > 0.5) {
            this.imageZoom = Math.round((this.imageZoom - 0.25) * 100) / 100;
        }
    }

    resetZoom() {
        this.imageZoom = 1;
    }

    startPhotoDrag(event: MouseEvent, viewer: HTMLElement) {
        if (event.button !== 0) {
            return;
        }

        this.isDraggingPhoto = true;
        this.photoDragStartX = event.clientX;
        this.photoDragStartY = event.clientY;
        this.photoDragScrollLeft = viewer.scrollLeft;
        this.photoDragScrollTop = viewer.scrollTop;
        event.preventDefault();
    }

    movePhotoDrag(event: MouseEvent, viewer: HTMLElement) {
        if (!this.isDraggingPhoto) {
            return;
        }

        viewer.scrollLeft = this.photoDragScrollLeft - (event.clientX - this.photoDragStartX);
        viewer.scrollTop = this.photoDragScrollTop - (event.clientY - this.photoDragStartY);
        event.preventDefault();
    }

    stopPhotoDrag() {
        this.isDraggingPhoto = false;
    }

    getVoucherImageUrl(voucher: any) {
        if (isNullOrUndefined(voucher) || isNullOrUndefined(voucher.FileName) || voucher.FileName === '') {
            return '';
        }

        if (voucher.FileName.indexOf('http://') === 0
            || voucher.FileName.indexOf('https://') === 0
            || voucher.FileName.indexOf('data:') === 0) {
            return voucher.FileName;
        }

        return `${environment.baseUrl}/soc-driver/v2/images/${encodeURIComponent(voucher.FileName)}`;
    }

    hasVoucherImage(voucher: any) {
        return this.getVoucherImageUrl(voucher) !== '';
    }

    getVoucherIds(voucher: any) {
        if (isNullOrUndefined(voucher) || isNullOrUndefined(voucher.VoucherIds)) {
            return [];
        }

        if (Array.isArray(voucher.VoucherIds)) {
            return voucher.VoucherIds;
        }

        return this.parseVoucherIds(voucher.VoucherIds);
    }

    getAmount(value: any) {
        const amount = Number(value);
        if (isNaN(amount)) {
            return '-';
        }

        return amount.toFixed(2);
    }

    shouldEditVoucherId() {
        return this.voucherEditMode === 'voucherId';
    }

    shouldEditVoucherIds() {
        return this.voucherEditMode === 'voucherIds';
    }

    setVoucherTab(tab: string) {
        this.activeVoucherTab = tab;
        this.stopPhotoDrag();
    }

    isVoucherTabActive(tab: string) {
        return this.activeVoucherTab === tab;
    }

    getVoucherOrderWrapper(voucher: any) {
        if (isNullOrUndefined(voucher)) {
            return undefined;
        }

        return voucher.Order || voucher.CompleteOrder || voucher.OrderDetails || voucher.OrderInfo || undefined;
    }

    getOrder(orderWrapper: any) {
        if (isNullOrUndefined(orderWrapper)) {
            return undefined;
        }

        return orderWrapper.Order || orderWrapper;
    }

    getInitialOrderRequest(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.InitialOrderRequest;
    }

    getPickupAddress(orderWrapper: any) {
        const request = this.getInitialOrderRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.PickupAddress;
    }

    getDestinationAddress(orderWrapper: any) {
        const request = this.getInitialOrderRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.DestinationAddress;
    }

    getStops(orderWrapper: any) {
        const request = this.getInitialOrderRequest(orderWrapper);
        if (isNullOrUndefined(request) || isNullOrUndefined(request.Stops)) {
            return [];
        }

        return request.Stops;
    }

    getOrderIdentifier(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return !isNullOrUndefined(order) && !isNullOrUndefined(order.OrderIdentifier) ? order.OrderIdentifier : '-';
    }

    getOrderStartedDate(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return !isNullOrUndefined(order) ? order.EntryDate : undefined;
    }

    getOrderFinishedDate(orderWrapper: any) {
        if (!isNullOrUndefined(orderWrapper) && !isNullOrUndefined(orderWrapper.CloseDate_iso)) {
            return orderWrapper.CloseDate_iso;
        }

        if (!isNullOrUndefined(orderWrapper) && !isNullOrUndefined(orderWrapper.CloseDate)) {
            return orderWrapper.CloseDate;
        }

        const order = this.getOrder(orderWrapper);
        if (!isNullOrUndefined(order) && !isNullOrUndefined(order.CloseDate)) {
            return order.CloseDate;
        }

        return undefined;
    }

    getOrderState(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (!isNullOrUndefined(orderWrapper) && !isNullOrUndefined(orderWrapper.CloseReason)) {
            return orderWrapper.CloseReason;
        }

        return !isNullOrUndefined(order) && !isNullOrUndefined(order.CloseReason) ? order.CloseReason : '-';
    }

    getOrderPlatform(orderWrapper: any) {
        const request = this.getInitialOrderRequest(orderWrapper);
        return !isNullOrUndefined(request) && !isNullOrUndefined(request.DevicePlatform)
            ? getOrderSourceLabel(request.DevicePlatform)
            : '-';
    }

    getOrderCustomer(orderWrapper: any) {
        const request = this.getInitialOrderRequest(orderWrapper);
        if (isNullOrUndefined(request) || isNullOrUndefined(request.Customer)) {
            return '-';
        }

        return request.Customer.Name || request.Customer.PhoneNumber || request.Customer.Phone || '-';
    }

    getOrderAssignedCar(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order) || isNullOrUndefined(order.AssignedCar)) {
            return '-';
        }

        const companyIdentifier = order.AssignedCar.CompanyIdentifier || '';
        const carId = order.AssignedCar.CarId || '';
        const combinedIdentifier = `${companyIdentifier}${carId}`;

        return combinedIdentifier !== '' ? combinedIdentifier : order.AssignedCar.CarIdentifier || '-';
    }

    getOrderCarNumber(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order) || isNullOrUndefined(order.AssignedCar)) {
            return '-';
        }

        return order.AssignedCar.CarNumber || '-';
    }

    getOrderDriverName(orderWrapper: any) {
        const driver = this.getOrderDriver(orderWrapper);
        return !isNullOrUndefined(driver) && !isNullOrUndefined(driver.DriverName) && driver.DriverName !== ''
            ? driver.DriverName
            : '-';
    }

    getOrderDriverPhone(orderWrapper: any) {
        const driver = this.getOrderDriver(orderWrapper);
        const driverPhone = !isNullOrUndefined(driver) ? this.normalizePhoneNumber(driver.DriverPhone) : '';
        return driverPhone !== ''
            ? driverPhone
            : '-';
    }

    normalizePhoneNumber(phoneNumber: any): string {
        if (isNullOrUndefined(phoneNumber)) {
            return '';
        }

        const trimmedPhoneNumber = String(phoneNumber).trim();
        const prefix = trimmedPhoneNumber.charAt(0) === '+' ? '+' : '';
        const digits = trimmedPhoneNumber.replace(/\D/g, '');

        return digits === '' ? '' : prefix + digits;
    }

    getOrderDriver(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order) || isNullOrUndefined(order.AssignedCar)) {
            return undefined;
        }

        return order.AssignedCar.Driver;
    }

    getOrderPrice(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order)) {
            return '-';
        }

        const price = Number(order.TaximeterTotalPrice);
        return isNaN(price) || price === 0 ? '-' : price.toFixed(2) + ' lei';
    }

    getOrderDistance(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order)) {
            return '-';
        }

        const distance = Number(order.BillableDistance);
        return isNaN(distance) || distance === 0 ? '-' : (distance / 1000).toFixed(2) + ' km';
    }

    getRoutePoints(orderWrapper: any) {
        const points = [];
        points.push({label: 'Pickup', address: this.getPickupAddress(orderWrapper), marker: 'P', type: 'pickup'});
        this.getStops(orderWrapper).forEach((stop, index) => {
            points.push({label: 'Stop ' + (index + 1), address: stop, marker: String(index + 1), type: 'stop'});
        });
        points.push({
            label: 'Destination',
            address: this.getDestinationAddress(orderWrapper),
            marker: 'D',
            type: 'destination'
        });

        return points.filter(point => !isNullOrUndefined(point.address));
    }

    formatAddress(address: any) {
        if (isNullOrUndefined(address)) {
            return '-';
        }

        if (!isNullOrUndefined(address.CompleteAddress) && address.CompleteAddress !== '') {
            return address.CompleteAddress;
        }

        const parts = [];
        if (!isNullOrUndefined(address.StreetName) && address.StreetName !== '') {
            parts.push(address.StreetName);
        }
        if (!isNullOrUndefined(address.StreetNumber) && address.StreetNumber !== '') {
            parts.push('Nr. ' + address.StreetNumber);
        }
        if (!isNullOrUndefined(address.BlocNumber) && address.BlocNumber !== '') {
            parts.push('Bl. ' + address.BlocNumber);
        }
        if (!isNullOrUndefined(address.ScaraNumber) && address.ScaraNumber !== '') {
            parts.push('Sc. ' + address.ScaraNumber);
        }
        if (parts.length === 0 && !isNullOrUndefined(address.Locality) && address.Locality !== '') {
            parts.push(address.Locality);
        }

        return parts.length === 0 ? '-' : parts.join(', ');
    }

    isPayMessageError() {
        return this.payMessageType === 'error';
    }

    openSelectedOrderMapInfo(mapMarker: MapMarker, marker: any) {
        this.selectedOrderInfoMarker = marker;
        this.voucherInfoWindow.open(mapMarker);
    }

    fitSelectedOrderMapBounds() {
        if (!this.shouldFitSelectedOrderMap || !this.voucherOrderMap
            || this.selectedOrderMapMarkers.length < 2) {
            return;
        }

        const bounds = this.getMapBounds(this.selectedOrderMapMarkers);
        this.shouldFitSelectedOrderMap = false;
        this.voucherOrderMap.fitBounds(bounds, 48);
    }

    private getVouchersFromResponse(res: any) {
        if (Array.isArray(res)) {
            return res;
        }

        if (!this.dataService.isResponseSuccessful(res)) {
            return [];
        }

        if (!isNullOrUndefined(res) && Array.isArray(res.PendingVouchers)) {
            return res.PendingVouchers;
        }

        if (!isNullOrUndefined(res) && Array.isArray(res.Data)) {
            return res.Data;
        }

        return [];
    }

    private resetSelectedVoucher() {
        this.selectedVoucher = undefined;
        this.selectedVoucherSource = undefined;
        this.editableVoucherIds = '';
        this.voucherEditMode = '';
        this.activeVoucherTab = 'payment';
        this.clearSelectedOrderData();
        this.imageZoom = 1;
        this.stopPhotoDrag();
    }

    private setSelectedOrderData(voucher: any) {
        this.clearSelectedOrderData();
        this.selectedVoucherOrderWrapper = this.getVoucherOrderWrapper(voucher);

        if (isNullOrUndefined(this.selectedVoucherOrderWrapper)) {
            return;
        }

        this.selectedOrderSummary = {
            identifier: this.getOrderIdentifier(this.selectedVoucherOrderWrapper),
            started: this.getOrderStartedDate(this.selectedVoucherOrderWrapper),
            finished: this.getOrderFinishedDate(this.selectedVoucherOrderWrapper),
            state: this.getOrderState(this.selectedVoucherOrderWrapper),
            platform: this.getOrderPlatform(this.selectedVoucherOrderWrapper),
            customer: this.getOrderCustomer(this.selectedVoucherOrderWrapper),
            assignedCar: this.getOrderAssignedCar(this.selectedVoucherOrderWrapper),
            carNumber: this.getOrderCarNumber(this.selectedVoucherOrderWrapper),
            driverName: this.getOrderDriverName(this.selectedVoucherOrderWrapper),
            driverPhone: this.getOrderDriverPhone(this.selectedVoucherOrderWrapper),
            price: this.getOrderPrice(this.selectedVoucherOrderWrapper),
            distance: this.getOrderDistance(this.selectedVoucherOrderWrapper)
        };

        this.selectedOrderRoutePoints = this.getRoutePoints(this.selectedVoucherOrderWrapper)
            .map(point => ({
                label: point.label,
                address: point.address,
                formattedAddress: this.formatAddress(point.address),
                marker: point.marker,
                type: point.type
            }));

        this.selectedOrderMapMarkers = this.selectedOrderRoutePoints
            .map((point) => {
                const coords = this.getAddressCoords(point.address);
                return isNullOrUndefined(coords) ? undefined : {
                    lat: coords.lat,
                    lng: coords.lng,
                    labelOptions: {
                        color: this.getRouteMarkerLabelColor(point.type),
                        fontSize: '13px',
                        fontWeight: '900',
                        text: point.marker
                    },
                    position: {lat: coords.lat, lng: coords.lng},
                    options: {icon: this.getRouteMarkerIconUrl(point.type)},
                    title: point.label + ': ' + point.formattedAddress
                };
            })
            .filter(marker => !isNullOrUndefined(marker));

        if (this.selectedOrderMapMarkers.length > 0) {
            this.centerOrderMap();
            this.selectedOrderMapZoom = this.selectedOrderMapMarkers.length > 1 ? 12 : 15;
            this.shouldFitSelectedOrderMap = this.selectedOrderMapMarkers.length > 1;
        }
    }

    private centerOrderMap() {
        const totals = this.selectedOrderMapMarkers.reduce((sum, marker) => ({
            lat: sum.lat + marker.lat,
            lng: sum.lng + marker.lng
        }), {lat: 0, lng: 0});

        this.selectedOrderMapLat = totals.lat / this.selectedOrderMapMarkers.length;
        this.selectedOrderMapLng = totals.lng / this.selectedOrderMapMarkers.length;
        this.selectedOrderMapCenter = {lat: this.selectedOrderMapLat, lng: this.selectedOrderMapLng};
    }

    private getRouteMarkerIconUrl(type: string) {
        if (type === 'pickup') {
            return 'https://maps.google.com/mapfiles/ms/icons/green-dot.png';
        }

        if (type === 'destination') {
            return 'https://maps.google.com/mapfiles/ms/icons/red-dot.png';
        }

        return 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png';
    }

    private getRouteMarkerLabelColor(type: string) {
        return type === 'stop' ? '#111827' : '#ffffff';
    }

    private clearSelectedOrderData() {
        this.selectedVoucherOrderWrapper = undefined;
        this.selectedOrderSummary = undefined;
        this.selectedOrderRoutePoints = [];
        this.selectedOrderMapMarkers = [];
        this.selectedOrderMapLat = 44.429021;
        this.selectedOrderMapLng = 26.103849;
        this.selectedOrderMapCenter = {lat: this.selectedOrderMapLat, lng: this.selectedOrderMapLng};
        this.selectedOrderMapZoom = 13;
        this.selectedOrderInfoMarker = undefined;
        this.shouldFitSelectedOrderMap = false;
    }

    private getMapBounds(points: Array<any>): google.maps.LatLngBoundsLiteral {
        return points.reduce((bounds, point) => ({
            north: Math.max(bounds.north, point.lat),
            south: Math.min(bounds.south, point.lat),
            east: Math.max(bounds.east, point.lng),
            west: Math.min(bounds.west, point.lng)
        }), {
            north: points[0].lat,
            south: points[0].lat,
            east: points[0].lng,
            west: points[0].lng
        });
    }

    private getAddressCoords(address: any) {
        if (isNullOrUndefined(address)) {
            return undefined;
        }

        if (!isNullOrUndefined(address.Coords)) {
            return this.parseCoords(address.Coords);
        }

        return this.parseCoords(address);
    }

    private parseCoords(coords: any) {
        if (isNullOrUndefined(coords)) {
            return undefined;
        }

        const lat = Number(coords.GpsLat || coords.Latitude || coords.latitude || coords.Latitudine || coords.lat);
        const lng = Number(coords.GpsLong || coords.Longitude || coords.longitude || coords.Longitudine || coords.lng);

        if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
            return undefined;
        }

        return {lat, lng};
    }

    private setPayMessage(message: string, type: string) {
        this.payMessage = message;
        this.payMessageType = type;
    }

    private clearPayMessage() {
        this.payMessage = '';
        this.payMessageType = '';
    }

    private buildPayVoucherPayload(voucher: any) {
        const payload = Object.assign({}, voucher);
        const amount = Number(payload.Amount);

        payload.Amount = isNaN(amount) ? payload.Amount : amount;
        if (this.shouldEditVoucherId()) {
            payload.VoucherId = isNullOrUndefined(payload.VoucherId) ? '' : String(payload.VoucherId).trim();
            delete payload.VoucherIds;
        } else if (this.shouldEditVoucherIds()) {
            payload.VoucherIds = this.parseVoucherIds(this.editableVoucherIds);
            delete payload.VoucherId;
        } else {
            delete payload.VoucherId;
            delete payload.VoucherIds;
        }

        return payload;
    }

    private getVoucherEditMode(voucher: any) {
        if (!isNullOrUndefined(voucher) && !isNullOrUndefined(voucher.VoucherId) && String(voucher.VoucherId).trim() !== '') {
            return 'voucherId';
        }

        return this.getVoucherIds(voucher).length > 0 ? 'voucherIds' : '';
    }

    private formatVoucherIds(voucher: any) {
        return this.getVoucherIds(voucher).join('\n');
    }

    private parseVoucherIds(value: any) {
        if (isNullOrUndefined(value)) {
            return [];
        }

        if (Array.isArray(value)) {
            return value.map(item => String(item).trim()).filter(item => item !== '');
        }

        return String(value)
            .split(/[\n,;]+/)
            .map(item => item.trim())
            .filter(item => item !== '');
    }

    private isPayResponseSuccessful(res: any) {
        if (this.dataService.isResponseSuccessful(res)) {
            return true;
        }

        if (typeof res === 'string') {
            try {
                return this.dataService.isResponseSuccessful(JSON.parse(res));
            } catch (error) {
                return res.indexOf('"StatusCode":0') >= 0 || res.indexOf('StatusCode: 0') >= 0;
            }
        }

        return false;
    }

    private getResponseMessage(res: any, fallback: string) {
        const parsedResponse = this.parseResponse(res);
        const nestedErrorResponse = !isNullOrUndefined(parsedResponse) && !isNullOrUndefined(parsedResponse.error)
            ? this.parseResponse(parsedResponse.error)
            : null;
        const responseMessage = this.getMessageFromParsedResponse(nestedErrorResponse)
            || this.getMessageFromParsedResponse(parsedResponse);

        if (responseMessage !== '') {
            return responseMessage;
        }

        if (!isNullOrUndefined(res) && !isNullOrUndefined(res.message) && String(res.message).trim() !== '') {
            return res.message;
        }

        return fallback;
    }

    private getMessageFromParsedResponse(res: any) {
        if (!isNullOrUndefined(res) && !isNullOrUndefined(res.StatusMessage)
            && String(res.StatusMessage).trim() !== '') {
            return res.StatusMessage;
        }

        if (!isNullOrUndefined(res) && !isNullOrUndefined(res.Message)
            && String(res.Message).trim() !== '') {
            return res.Message;
        }

        return '';
    }

    private parseResponse(res: any) {
        if (typeof res !== 'string') {
            return res;
        }

        try {
            return JSON.parse(res);
        } catch (error) {
            return null;
        }
    }
}
